import React, { Fragment, useEffect, useState } from 'react';
import { Field, Form, Input } from '@availity/form';
import Loader from 'react-loaders';
import { createSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { useStateSelector } from '../../../store/selectors';
import { USER_ROLES } from '../../../utils/constants';
import { Col, Label, Row, Input as ReactstrapInput, Button } from 'reactstrap';
import { Breadcrumb, BreadcrumbItem, Card, CardBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faExclamationCircle,
    faGear,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { HomeButton } from '../../../components/HomeButton/home-button';
import './TimelineEditor.scss';
import * as yup from 'yup';
import TimelineTable from './TimelineTable';
import { MAX_DEPENDENCIES_AMOUNT } from '../Constants/constants';
import createNotification from '../../../utils/createNotification';
import EditableNameField from './EditableNameField/EditableNameField';
import StageEditorModal from './StageEditorModal/StageEditorModal';
import { MarketplaceTemplatePreviewComponent } from '../MarketplaceTemplatePreview/MarketplaceTemplatePreview.component';
import { useParams } from 'react-router-dom';
import { FieldArray } from 'formik';
import { TimelineEventSortingService } from './TimelineEventSortingService';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useDispatch } from 'react-redux';
import { GetTimelineStages } from '../../../slices/marketplace/timeline/thunks/get-timeline-stages.thunk';
import { TimelineValidationHelper } from './TimelineValidationHelper';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import {
    resetCurrentTimeline,
    udateCurrentTimelineDocumentSections,
} from '../../../slices/marketplace/timeline/timeline.slice';
import { DocumentDetails } from '../../../slices/marketplace/timeline/models/document-details.model';
import { AdminMarketplaceTab } from '../../../slices/marketplace/timeline/models/enums/admin-marketplace-tab.enum';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import {
    TimelineParty,
    TimelineParties,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineAction } from '../../../slices/marketplace/timeline/models/timeline-actions.model';
import {
    TimelineEventDependency,
    initTimelineEventDependency,
} from '../../../slices/marketplace/timeline/models/timeline-event-dependency.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineStage } from '../../../slices/marketplace/timeline/models/timeline-stage.model';
import {
    DocumentSection,
    GenerateDocumentSectionList,
} from '../../../slices/marketplace/timeline/models/document-section.model';
import { ShowFirstThenFullTextOnHoverLabel } from '../../../components/ShowFirstThenFullTextOnHoverLabel/ShowFirstThenFullTextOnHoverLabel';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import {
    NdaTermSelection,
    NdaTermSelectionDocumentTemplatesState,
} from '../../../slices/marketplace/timeline/models/nda-term-selection';
import { NdaTermSelectionType } from '../../../slices/marketplace/timeline/models/enums/nda-term-selection-type';
import { NdaTermStep } from '../../../slices/marketplace/timeline/models/enums/nda-term-step';
import { EngagementDocumentSource } from '../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import {
    TimelineTempo,
    TimelineTempos,
} from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import {
    LevelOfService,
    LevelOfServiceOptions,
    LevelsOfService,
} from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { BuyerExpressionOfInterestSettings } from '../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';
import { generateGuid } from '../../../utils/typedHelper';

interface TimelineEditorProps {
    type: 'engagement' | 'template';
}

interface TimelineEventFields {
    eventName: string;
    stage: string;
    party: string;
    duration: string;
    dependencies: TimelineEventDependencyFields[];
}

interface TimelineEventDependencyFields {
    dependencyEventId?: string;
    offsetDays: number;
}

const TimelineEditor = (props: TimelineEditorProps) => {
    const isTemplate = props.type === 'template';
    const editorType = isTemplate ? 'Timeline Template' : 'Timeline';
    const dispatch = useDispatch() as any;
    const userSummary = useStateSelector((state) => state.userSummary.summary);
    const axios = useStateSelector((state) => state.core.axios);
    const timelineStages = useStateSelector(
        (state) => state.timelines.timelineStages
    );
    const currentTimeline = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const navigate = useNavigate();

    const timelineId = useParams().id;
    const newTemplate = timelineId == 'new';

    const isAdmin = userSummary.roleId === USER_ROLES.ADMIN;

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [initialEvents, setInitialEvents] = useState<TimelineEvent[]>([]);
    const [selectedTempo, setSelectedTempo] = useState<TimelineTempo>(
        TimelineTempo.MEDIUM
    );
    const [currentTemplateTempo, setCurrentTemplateTempo] =
        useState<TimelineTempo>(TimelineTempo.MEDIUM);

    const [currentLevelOfService, setCurrentLevelOfService] =
        useState<LevelOfService>(LevelOfService.DiscountBrokerage);
    const [levelOfServiceToBeSet, setLevelOfServiceToBeSet] =
        useState<LevelOfService>(undefined);

    const [documentDetailsList, setDocumentDetailsList] = useState<
        DocumentDetails[]
    >([]);

    const [dataRequestProjects, setDataRequestProjects] = useState<
        LinkedDataRequestProject[]
    >([]);
    const [ndaTermSelectionState, setNdaTermSelectionState] =
        useState<NdaTermSelection>(null);

    const [stagesOrder, setStagesOrder] = useState<number[]>([]);

    const [templateVersion, setTemplateVersion] = useState<number[]>(null);

    const defaultTimelineName = editorType;
    const [timelineName, setTimelineName] = useState('');
    const [initialTimelineName, setInitialTimelineName] = useState('');

    const [nameIsValid, setNameIsValid] = useState(!newTemplate);
    const [nameIsEditable, setNameIsEditable] = useState(newTemplate);

    const [stages, setStages] = useState<TimelineStage[]>([]);
    const [stagesAreLoading, setStagesAreLoading] = useState(false);
    const [selectedStage, setSelectedStage] = useState(0);

    const [isStageEditorModalVisible, setIsStageEditorModalVisible] =
        useState(false);
    const [flagToFetchStages, setFlagToFetchStages] = useState(false);

    const [previewIsVisible, setPreviewIsVisible] = useState(false);

    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);
    const [timelinePracticesName, setTimelinePracticesName] = useState<
        string[]
    >([]);
    const [timelineTemplateName, setTimelineTemplateName] = useState('');
    const [timelineStartDate, setTimelineStartDate] = useState<Date>(null);
    const [isArchived, setIsArchived] = useState(false);

    const [documentSectionList, setDocumentSectionList] = useState<
        DocumentSection[]
    >(GenerateDocumentSectionList());
    const [initialDocumentSectionList, setInitialDocumentSectionList] =
        useState<DocumentSection[]>([]);

    const [isTeaserConfirmed, setisTeaserConfirmed] = useState(false);
    const [teaserData, setTeaserData] = useState<TeaserPracticeData[]>();
    const [initialTeaserData, setInitialTeaserData] =
        useState<TeaserPracticeData[]>();

    const [expressionOfInterestSettings, setExpressionOfInterestSettings] =
        useState<BuyerExpressionOfInterestSettings>(null);
    const [
        initialExpressionOfInterestSettings,
        setInitialExpressionOfInterestSettings,
    ] = useState<BuyerExpressionOfInterestSettings>(null);

    const [
        tempoUpdateConfirmationModalVisible,
        setTempoUpdateConfirmationModalVisible,
    ] = useState(false);

    const areEqualArrays = (a: any[], b: any[]) => {
        if (a.length !== b.length) return false;
        return a.every((item, index) => {
            const otherItem = b[index];
            return JSON.stringify(item) === JSON.stringify(otherItem);
        });
    };

    const areEqual = (a: any, b: any) => {
        if (!!a != !!b) return false;
        return JSON.stringify(a) === JSON.stringify(b);
    };

    const fetchTimelineTemplate = (id: number) => {
        setIsLoading(true);
        dispatch(GetTimelineById({ id, isTemplate }))
            .then((response: any) => {
                setInitialDocumentSectionList(
                    response.payload.engagementData.documentSectionsList
                );
                if (response.meta.requestStatus != 'fulfilled') {
                    createNotification(
                        `There was an error while loading ${editorType}.`,
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    `There was an error while loading ${editorType}.`,
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (currentTimeline) {
            dispatch(udateCurrentTimelineDocumentSections(documentSectionList));
        }
    }, [documentSectionList]);

    useEffect(() => {
        if (timelineId) {
            if (newTemplate) {
                setTimelineName(defaultTimelineName);
            } else if ((Number(timelineId) || 0) > 0) {
                fetchTimelineTemplate(Number(timelineId));
            } else {
                createNotification(
                    `There was an error while loading ${editorType}.`,
                    'error'
                );
            }
        }
    }, []);

    useEffect(() => {
        if (currentTimeline) {
            if (currentTimeline.isTemplate != isTemplate) {
                if (currentTimeline.isTemplate) {
                    navigate(
                        `/marketplace/admin/templates/${currentTimeline.id}`
                    );
                    return;
                } else {
                    navigate(
                        `/marketplace/admin/engagements/${currentTimeline.id}`
                    );
                    return;
                }
            }
            if (!isTemplate) {
                setTimelineStartDate(
                    currentTimeline.engagementProcess.startDate
                );
                const names = currentTimeline.engagementProcess.practices.map(
                    (x) => x.practiceName
                );
                setTimelinePracticesName(names);
                setTimelineTemplateName(currentTimeline.templateName);
                setIsArchived(
                    currentTimeline.engagementProcess.status !==
                        EngagementProcessStatus.Prospective &&
                        currentTimeline.engagementProcess.status !==
                            EngagementProcessStatus.Current
                );
            }
            setInitialTimelineName(currentTimeline.name);
            setTimelineName(currentTimeline.name);

            setTemplateVersion(currentTimeline.version);
            setDocumentSectionList(
                currentTimeline.engagementData.documentSectionsList
            );

            setisTeaserConfirmed(
                currentTimeline.engagementData?.isTeaserConfirmed
            );
            setTeaserData(currentTimeline.engagementData?.teaserData);
            setInitialTeaserData(currentTimeline.engagementData?.teaserData);

            const eoiSetting =
                currentTimeline.engagementData?.expressionOfInterestSettings;
            setExpressionOfInterestSettings(eoiSetting);
            setInitialExpressionOfInterestSettings(eoiSetting);

            const dataNdaTermSelection =
                currentTimeline.engagementData.ndaTermSelectionList.find(
                    (x) => x.type === NdaTermSelectionType.DataNdaTerms
                );
            if (dataNdaTermSelection) {
                setNdaTermSelectionState(dataNdaTermSelection);
            }
            const mappedEvents = recalculateRows(
                currentTimeline.events.map(mapEvent)
            );

            const stagesOrder = getStagesOrder(mappedEvents);
            setStagesOrder(stagesOrder);

            setEvents(mappedEvents);
            setInitialEvents(mappedEvents);

            setDocumentDetailsList(
                currentTimeline.engagementData?.documentsList
            );
            setDataRequestProjects(
                currentTimeline.engagementData?.dataRequestProjects
            );
            setSelectedTempo(currentTimeline.tempo || TimelineTempo.MEDIUM);
            setCurrentTemplateTempo(
                currentTimeline.tempo || TimelineTempo.MEDIUM
            );
            setCurrentLevelOfService(
                currentTimeline.levelOfService ||
                    LevelOfService.DiscountBrokerage
            );
        }
    }, [
        currentTimeline?.name,
        currentTimeline?.templateName,
        currentTimeline?.version,
        currentTimeline?.isTemplate,
        currentTimeline?.id,
        currentTimeline?.events,
        currentTimeline?.engagementData?.documentsList,
        currentTimeline?.engagementData?.teaserData,
        currentTimeline?.engagementData?.expressionOfInterestSettings,
        currentTimeline?.engagementProcess,
    ]);

    const fetchStages = () => {
        setStagesAreLoading(true);
        dispatch(GetTimelineStages()).then(() => {
            setStagesAreLoading(false);
        });
    };

    useEffect(() => {
        fetchStages();
    }, [flagToFetchStages]);

    useEffect(() => {
        setStages(timelineStages);
    }, [timelineStages]);

    useEffect(() => {
        const nameChanged = timelineName !== initialTimelineName;
        const nameIsNotEmpty = timelineName.length > 0;

        const nameCanBeSaved =
            (nameChanged || !newTemplate) &&
            nameIsNotEmpty &&
            nameIsValid &&
            !nameIsEditable;
        const shouldSetBeEnabled =
            nameCanBeSaved &&
            (nameChanged ||
                newTemplate ||
                currentTemplateTempo !== currentTimeline.tempo ||
                currentLevelOfService !== currentTimeline.levelOfService ||
                !areEqualArrays(events, initialEvents) ||
                !areEqualArrays(
                    documentSectionList,
                    initialDocumentSectionList
                ) ||
                !areEqualArrays(teaserData, initialTeaserData) ||
                !areEqual(
                    expressionOfInterestSettings,
                    initialExpressionOfInterestSettings
                ));

        setIsSaveEnabled(shouldSetBeEnabled);
    }, [
        nameIsValid,
        currentTemplateTempo,
        currentLevelOfService,
        nameIsEditable,
        events,
        documentSectionList,
        teaserData,
    ]);

    const initialFormValues: TimelineEventFields = {
        eventName: '',
        stage: '',
        party: '',
        duration: '',
        dependencies: [{ dependencyEventId: '', offsetDays: 0 }],
    };

    const validationSchema = yup.object().shape({
        eventName: yup.string().trim().required(),
        stage: yup.string().trim().required(),
        party: yup.string().trim().required(),
        duration: yup.number().required().min(0, 'min 0'),
    });

    const doesNameExist = async (newName: string): Promise<boolean> => {
        try {
            const response = await axios.get<boolean>(
                `api/Marketplace/${
                    isTemplate ? 'TimelineTemplates' : 'Engagements'
                }/DoesExist/${encodeURIComponent(newName)}`
            );

            if (!response.data) {
                setTimelineName(newName);
                setNameIsValid(true);
                return false;
            } else {
                createNotification(
                    'A timeline with this name already exists.',
                    'error'
                );
                return true;
            }
        } catch (error) {
            createNotification(
                'An error occurred while checking the name. Please try again later.',
                'error'
            );
            return true;
        }
    };

    const saveTeaserToCurrentState = async (newValues: TeaserPracticeData[]) =>
        setTeaserData([...newValues]);

    const axiosRequest = (template: any) =>
        newTemplate
            ? axios.post('/api/Marketplace/TimelineTemplates', template)
            : axios.put(
                  `/api/Marketplace/${
                      isTemplate ? 'TimelineTemplates' : 'Engagements'
                  }/${timelineId}`,
                  template
              );

    const filterOutNonExistingOldDocuments = (
        event: TimelineEvent
    ): TimelineEvent => {
        const keys = [
            'Blinded',
            'DvmsBlinded',
            'StaffBlinded',
            'Visible',
        ] as const;

        const isValidDoc = (id: string) =>
            documentDetailsList.some((d) => d.id === id && !d.toRemove);

        const oldAddRefs =
            event.linkedAction?.oldReferenceItemAdditionalVersionIds;
        const oldRefs = event.linkedAction?.oldReferenceItemIds;

        if (!oldAddRefs && (!oldRefs || oldRefs.length === 0)) return event;

        const updatedOldRefs = oldRefs?.filter(isValidDoc) || [];

        const updatedOldAddRefs = keys.reduce<typeof oldAddRefs>(
            (acc, key) => {
                if (
                    Array.isArray(oldAddRefs[key]) &&
                    oldAddRefs[key]!.length > 0
                ) {
                    acc[key] = oldAddRefs[key]!.filter(isValidDoc);
                }
                return acc;
            },
            { ...oldAddRefs }
        );

        return {
            ...event,
            linkedAction: {
                ...event.linkedAction,
                oldReferenceItemIds: updatedOldRefs,
                oldReferenceItemAdditionalVersionIds: updatedOldAddRefs,
            },
        };
    };

    const handleSave = () => {
        if (
            !TimelineValidationHelper.areAllEventsTempoDurationsValid(events)
        ) {
            return;
        }

        setIsSaving(true);
        const timelineTemplate = {
            name: timelineName,
            events: events.map((event) =>
                filterOutNonExistingOldDocuments({
                    ...event,
                })
            ),
            documentDetailsList: documentDetailsList,
            documentSectionList: documentSectionList,
            dataRequestProjects: dataRequestProjects,
            ndaTermSelection: ndaTermSelectionState,
            teaserData: teaserData,
            expressionOfInterestSettings: expressionOfInterestSettings,
            isTeaserConfirmed: isTeaserConfirmed,
            version: templateVersion,
            tempo: currentTemplateTempo,
            levelOfService: currentLevelOfService,
        };

        axiosRequest(timelineTemplate)
            .then((response: any) => {
                if (response && response.status === 200) {
                    createNotification(
                        'Template successfully saved.',
                        'success'
                    );
                    if (Number(response.data) > 0) {
                        if (newTemplate) {
                            navigate(
                                `/marketplace/admin/templates/${response.data}`
                            );
                            fetchTimelineTemplate(Number(response.data));
                        }
                    } else {
                        fetchTimelineTemplate(Number(timelineId));
                    }
                    setIsSaveEnabled(false);
                } else {
                    const message = response.response.data.title;
                    createNotification(message || '', 'error');
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const mapEvent = (data: TimelineEvent) => {
        const result: TimelineEvent = {
            id: data.id ?? generateGuid(),
            stage: data.stage,
            name: data.name || 'Default Event',
            party: data.party || 0,
            dependencies: data.dependencies?.filter(
                (d: TimelineEventDependency) => d.dependencyEventId !== ''
            ),
            dayStart: data.dayStart,
            dayEnd: data.dayEnd,
            dayCompleted: data.dayCompleted,
            linkedAction: data.linkedAction,
            status: data.status ?? TimelineEventStatus.Upcoming,
            slowTempoDurationDays: data.slowTempoDurationDays || 0,
            mediumTempoDurationDays: data.mediumTempoDurationDays || 0,
            fastTempoDurationDays: data.fastTempoDurationDays || 0,
            currentTempoDurationDays: data.currentTempoDurationDays || 0,
            currentTempo: data.currentTempo ?? TimelineTempo.MEDIUM,
            isSuspendEvent: data.isSuspendEvent ?? false,
        };
        return result;
    };

    const handleFlagState = () => {
        setFlagToFetchStages(!flagToFetchStages);
    };

    const recalculateRows = (rows: TimelineEvent[]): TimelineEvent[] => {
        rows = TimelineEventSortingService.sortTimelineEvents(
            rows,
            currentLevelOfService
        );
        return rows;
    };

    const getStagesOrder = (rows: TimelineEvent[]): number[] => {
        const order = TimelineEventSortingService.getDistinctStages(rows);
        return order;
    };

    const handleAddRow = (data: any) => {
        try {
            const newEvent = mapEvent(data);
            const updatedRows = TimelineEventSortingService.placeUpdatedEvent(
                [...events],
                newEvent
            );
            const sortedEvents = recalculateRows([...updatedRows]);
            const newStagesOrder = getStagesOrder(sortedEvents);

            setEvents(sortedEvents);
            setStagesOrder(newStagesOrder);
            setSelectedStage(0);

            return true;
        } catch (error) {
            createNotification((error as Error).message, 'error');
            return false;
        }
    };

    const handleRemoveRow = (id: string) => {
        const eventToRemove = events.find((row) => row.id === id);
        let updatedRows = events.filter((row) => row.id !== id);

        const masterDoc = documentDetailsList.find(
            (d) => d.id == eventToRemove.linkedAction?.referenceItemId
        );
        const addDocIds =
            (!!masterDoc?.additionalVersionIds &&
                masterDoc?.additionalVersionIds) ||
            [];

        if (
            (eventToRemove.linkedAction?.actionType ===
                TimelineActionType.BuyerReviewDocument &&
                !eventToRemove.linkedAction?.actionReferenceId &&
                !!masterDoc) ||
            !!masterDoc ||
            addDocIds?.length > 0
        ) {
            handleDocumentsChanges(
                eventToRemove,
                'remove',
                null,
                masterDoc.id,
                null,
                addDocIds
            );
        }

        removeRelatedActionsAndDocumentsToProject(
            eventToRemove,
            undefined,
            updatedRows
        );

        handleNdaTermSelectionChanges(eventToRemove, 'remove', undefined);

        if (
            eventToRemove.linkedAction?.actionType ===
            TimelineActionType.SellerEngagementDocument
        ) {
            const referencingBuyerActionIndex = updatedRows.findIndex(
                (e) =>
                    e.party === TimelineParty.Buyer &&
                    e.linkedAction &&
                    e.linkedAction.actionReferenceId &&
                    e.linkedAction.actionReferenceId ==
                        eventToRemove.linkedAction?.id
            );
            if (referencingBuyerActionIndex > -1) {
                const newLinkedAction = {
                    ...updatedRows[referencingBuyerActionIndex].linkedAction,
                };
                newLinkedAction.referenceItemId = undefined;

                updatedRows[referencingBuyerActionIndex].linkedAction =
                    newLinkedAction;
            }
        }
        handleProjectsChanges(eventToRemove, null, null);

        updatedRows = updatedRows.map((row) => ({
            ...row,
            dependencies: row.dependencies.filter(
                (dependency) => dependency.dependencyEventId !== id
            ),
        }));

        try {
            const recalculatedRows = recalculateRows(updatedRows);
            const newStagesOrder = getStagesOrder(recalculatedRows);
            setEvents(recalculatedRows);
            setStagesOrder(newStagesOrder);
        } catch {
            createNotification(
                'Invalid dependencies and dependent combination.',
                'error'
            );
        }
    };

    const handleNdaTermSelectionChanges = (
        event: TimelineEvent,
        type: 'update' | 'remove',
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState
    ) => {
        if (
            type == 'remove' &&
            event.linkedAction?.actionType ===
                TimelineActionType.SellerCustomSelectDataNdaTerms
        ) {
            setNdaTermSelectionState(null);
        } else if (ndaTermSelection) {
            setNdaTermSelectionState({
                id: event.id,
                noNsTemplateId: ndaTermSelection.noNsTemplateId,
                dvmAndStaffNsTemplateId:
                    ndaTermSelection.dvmAndStaffNsTemplateId,
                dvmNsTemplateId: ndaTermSelection.dvmNsTemplateId,
                staffNsTemplateId: ndaTermSelection.staffNsTemplateId,
                isLockedIn: false,
                ndaTermsList: [],
                type: NdaTermSelectionType.DataNdaTerms,
                currentStep: NdaTermStep.InitialSelection,
            });
        }
    };

    const handleUpdateRow = (
        id: string,
        updatedValues: {
            name?: string;
            duration?: number;
            stage?: number;
            party?: TimelineParty;
            dependencies?: TimelineEventDependency[];
            linkedAction?: TimelineAction;
            status: TimelineEventStatus;
        },
        newMasterDocumentDetails?: DocumentDetails,
        masterDocumentToRemove?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        additionalDocumentsToRemove?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ): boolean => {
        let updatedRows = [...events];
        const rowIndex = events.findIndex((row) => row.id === id);
        if (rowIndex === -1) return false;
        const oldActionType = events[rowIndex]?.linkedAction?.actionType;

        const isCompletedEarlier =
            updatedValues.status === TimelineEventStatus.Completed &&
            events[rowIndex].status !== TimelineEventStatus.Completed;

        setExpressionOfInterestSettings(expressionOfInterestSettings);

        handleNdaTermSelectionChanges(
            events[rowIndex],
            !!masterDocumentToRemove ? 'remove' : 'update',
            ndaTermSelection
        );

        handleDocumentsChanges(
            events[rowIndex],
            !!masterDocumentToRemove ||
                (!!additionalDocumentsToRemove &&
                    additionalDocumentsToRemove.length > 0)
                ? 'remove'
                : 'update',
            newMasterDocumentDetails,
            masterDocumentToRemove?.id,
            additionalDocumentDetails,
            additionalDocumentsToRemove?.map((add) => add.id) ?? []
        );

        handleProjectsChanges(
            events[rowIndex],
            updatedValues.linkedAction,
            dataRequestProject
        );

        removeRelatedActionsAndDocumentsToProject(
            events[rowIndex],
            dataRequestProject?.id,
            updatedRows
        );

        let updatedRow = {
            ...events[rowIndex],
            ...updatedValues,
        };

        updatedRow.dependencies =
            updatedValues?.dependencies?.map((dependency) => ({
                ...dependency,
            })) ?? [];

        const isDuplicate = TimelineValidationHelper.isEventDuplicated(
            events,
            rowIndex,
            updatedRow.name,
            updatedRow.stage,
            updatedRow.party
        );

        if (isDuplicate) {
            return false;
        }

        const areDependenciesValid =
            TimelineValidationHelper.areDependenciesValid(
                events,
                updatedRow,
                stagesOrder
            );

        if (!areDependenciesValid) {
            return false;
        }

        try {
            updatedRows[rowIndex] = {
                ...updatedRow,
            };

            if (
                updatedRow.linkedAction?.actionType ===
                    TimelineActionType.SellerEngagementDocument &&
                oldActionType &&
                updatedRow.linkedAction?.actionType === oldActionType
            ) {
                const referencingBuyerActionIndex = updatedRows.findIndex(
                    (e) =>
                        e.party === TimelineParty.Buyer &&
                        e.linkedAction &&
                        e.linkedAction.actionReferenceId &&
                        e.linkedAction.actionReferenceId ==
                            updatedRow.linkedAction?.id
                );
                if (referencingBuyerActionIndex > -1) {
                    const newLinkedAction = {
                        ...updatedRows[referencingBuyerActionIndex]
                            .linkedAction,
                        referenceItemId:
                            updatedRow.linkedAction.referenceItemId,
                    };

                    updatedRows[referencingBuyerActionIndex].linkedAction =
                        newLinkedAction;
                }
            }

            if (isCompletedEarlier) {
                var reculculatedRows =
                    TimelineEventSortingService.recalculateCompletedEarlierEvent(
                        updatedRows,
                        updatedRow,
                        currentTimeline.engagementProcess.startDate
                    );
                updatedRows = [...reculculatedRows];
                updatedRow = {
                    ...updatedRows[rowIndex],
                };
            }

            updatedRows = TimelineEventSortingService.placeUpdatedEvent(
                updatedRows,
                updatedRow
            );

            const recalculatedRows = recalculateRows(updatedRows);
            const newStagesOrder = getStagesOrder(recalculatedRows);
            setEvents(recalculatedRows);
            setStagesOrder(newStagesOrder);
        } catch (error) {
            createNotification((error as Error).message, 'error');
            return false;
        }
        return true;
    };

    const validateAndAddEvent = (values: TimelineEventFields) => {
        const stageId = Number(values.stage);
        const partyId = Number(values.party);
        const duration = Number(values.duration);

        const stageExists = stages.some((stage) => stage.id === stageId);
        if (!stageExists) {
            createNotification(
                'Selected stage does not exist in the list of stages',
                'error'
            );
            return false;
        }

        const eventExists = events.some(
            (e) =>
                e.name === values.eventName &&
                e.stage === stageId &&
                e.party === partyId &&
                e.currentTempoDurationDays === duration
        );
        if (eventExists) {
            createNotification(
                'This combination of name, stage and duration already exists',
                'error'
            );
            return false;
        }

        return handleAddRow({
            stage: stageId,
            name: values.eventName || 'Default Event Name',
            party: partyId,
            slowTempoDurationDays: duration,
            mediumTempoDurationDays: duration,
            fastTempoDurationDays: duration,
            currentTempoDurationDays: duration,
            currentTempo: currentTemplateTempo,
            dependencies: values.dependencies.map((df) =>
                initTimelineEventDependency(df.dependencyEventId, df.offsetDays)
            ),
        });
    };

    const removeRelatedActionsAndDocumentsToProject = (
        event: TimelineEvent,
        newProjectId: string,
        updatedEvents: TimelineEvent[]
    ) => {
        const linkedAction = event.linkedAction;
        if (
            linkedAction?.actionType !==
                TimelineActionType.SellerDataAndDocumentRequest ||
            linkedAction?.referenceItemId === newProjectId
        )
            return;
        const projectId = linkedAction.referenceItemId;

        const documentsToRemove = documentDetailsList.filter(
            (doc) =>
                doc.source === EngagementDocumentSource.Questionnaire &&
                doc.linkedProjectId === projectId
        );

        // filter out all events related to documents that are linked to the old project
        const eventsWithDocumentsToRemove = updatedEvents.filter((e) =>
            documentsToRemove.some(
                (doc) => doc.id === e.linkedAction?.referenceItemId
            )
        );
        const buyerReviewEventRelatedToEventsWithDocumentsToRemove =
            updatedEvents.filter(
                (e) =>
                    e.linkedAction?.actionType ===
                        TimelineActionType.BuyerReviewDocument &&
                    e.linkedAction?.actionReferenceId &&
                    eventsWithDocumentsToRemove.some(
                        (ev) =>
                            ev.linkedAction?.id ===
                            e.linkedAction?.actionReferenceId
                    )
            );
        const allEventsToRemoveActions = [
            ...eventsWithDocumentsToRemove,
            ...buyerReviewEventRelatedToEventsWithDocumentsToRemove,
        ];
        updatedEvents.forEach((e) => {
            if (
                allEventsToRemoveActions.some(
                    (ev) => ev.id === e.id && ev.linkedAction
                )
            ) {
                e.linkedAction = undefined;
            }
        });

        // filter out documents that are linked to the old project
        setDocumentDetailsList((prevList) =>
            prevList.map((document) =>
                documentsToRemove.some(
                    (docToRemove) => docToRemove.id === document.id
                )
                    ? { ...document, toRemove: true }
                    : document
            )
        );
    };

    const handleDocumentsChanges = (
        event: TimelineEvent,
        type: 'update' | 'remove',
        newMasterDocumentDetails: DocumentDetails,
        masterDocumentIdToRemove?: string,
        newAdditionalDocumentDetails?: DocumentDetails[],
        additionalDocumentIdsToRemove?: string[]
    ) => {
        if (type == 'remove') {
            const updatedDocs = RemoveMasterDocument(
                event,
                newMasterDocumentDetails,
                masterDocumentIdToRemove
            );
            RemoveAdditionalDocuments(
                event,
                newAdditionalDocumentDetails,
                additionalDocumentIdsToRemove,
                updatedDocs
            );
        } else if (
            !!newMasterDocumentDetails ||
            (!!newAdditionalDocumentDetails &&
                newAdditionalDocumentDetails.length > 0)
        )
            UpdateDocuments(event, [
                newMasterDocumentDetails,
                ...(newAdditionalDocumentDetails ?? []),
            ]);
    };

    const UpdateDocuments = (
        event: TimelineEvent,
        newDocumentDetails: DocumentDetails[]
    ) => {
        let updatedDocumentsList = !!documentDetailsList
            ? [...documentDetailsList]
            : [];
        const updatedDocListIdsSet = new Set(
            updatedDocumentsList.map((d) => d.id)
        );

        const newDocsInOldList = newDocumentDetails.filter((d) =>
            updatedDocListIdsSet.has(d.id)
        );
        if (newDocsInOldList.length > 0) {
            updatedDocumentsList = updatedDocumentsList.map((ud) => {
                const newDoc = newDocsInOldList.find((od) => od.id == ud.id);
                return newDoc ?? ud;
            });
        }

        const docsToConcat = newDocumentDetails.filter(
            (d) => !updatedDocListIdsSet.has(d.id)
        );
        updatedDocumentsList = updatedDocumentsList.concat(docsToConcat);

        setDocumentDetailsList(() => updatedDocumentsList);
    };

    const RemoveAdditionalDocuments = (
        event: TimelineEvent,
        newAdditionalDocumentDetails?: DocumentDetails[],
        additionalDocumentIdsToRemove?: string[],
        updatedDocumentDetails: DocumentDetails[] = []
    ) => {
        if (
            (!newAdditionalDocumentDetails ||
                newAdditionalDocumentDetails.length == 0) &&
            (!additionalDocumentIdsToRemove ||
                additionalDocumentIdsToRemove.length == 0)
        )
            return;

        let additionalDocsUpdatedStatues =
            !additionalDocumentIdsToRemove ||
            additionalDocumentIdsToRemove?.length == 0
                ? []
                : additionalDocumentIdsToRemove.map((id) => {
                      return {
                          id: id,
                          isUpdated: !!newAdditionalDocumentDetails?.some(
                              (add) => add.id == id
                          ),
                      };
                  });

        let updatedDocumentsList = updatedDocumentDetails
            ? [...updatedDocumentDetails]
            : [];

        if (additionalDocsUpdatedStatues.some((s) => s.isUpdated)) {
            updatedDocumentsList = updatedDocumentsList.map((u) =>
                additionalDocsUpdatedStatues.find((au) => au.id == u.id)
                    .isUpdated
                    ? newAdditionalDocumentDetails?.find(
                          (nad) => nad.id == u.id
                      )
                    : u
            );

            additionalDocsUpdatedStatues = additionalDocsUpdatedStatues.filter(
                (s) => s.isUpdated
            );
        }

        if (
            additionalDocsUpdatedStatues.length > 0 &&
            additionalDocsUpdatedStatues.every((s) => !s.isUpdated)
        ) {
            // also mark old additional document versions as to remove
            const oldAdditionalDocumentVersionIds = Object.values(
                event.linkedAction.oldReferenceItemAdditionalVersionIds
            ).reduce((acc: string[], ids) => {
                const newAcc = [...acc, ...ids];
                return newAcc;
            }, [] as string[]);

            additionalDocsUpdatedStatues = [
                ...additionalDocsUpdatedStatues,
                ...oldAdditionalDocumentVersionIds.map((id) => ({
                    id,
                    isUpdated: false,
                })),
            ];

            additionalDocsUpdatedStatues.forEach(
                (s) =>
                    (updatedDocumentsList = markDocumentToRemove(
                        updatedDocumentsList,
                        s.id
                    ))
            );
        }

        const docsToConcat = newAdditionalDocumentDetails?.filter((d) =>
            updatedDocumentsList.every((doc) => doc.id !== d.id)
        );
        if (docsToConcat) {
            updatedDocumentsList = updatedDocumentsList.concat(docsToConcat);
        }
        setDocumentDetailsList(() => updatedDocumentsList);
    };

    const RemoveMasterDocument = (
        event: TimelineEvent,
        newMasterDocumentDetails: DocumentDetails,
        masterDocumentIdToRemove?: string
    ) => {
        const isMasterDocUpdated =
            !!masterDocumentIdToRemove &&
            masterDocumentIdToRemove === newMasterDocumentDetails?.id;
        const oldReferenceActionItemId = event?.linkedAction?.referenceItemId;

        let updatedDocumentsList = documentDetailsList
            ? [...documentDetailsList]
            : [];
        if (isMasterDocUpdated) {
            updatedDocumentsList = updatedDocumentsList.map((u) =>
                u.id === masterDocumentIdToRemove ? newMasterDocumentDetails : u
            );
        }

        if (!isMasterDocUpdated && !!newMasterDocumentDetails) {
            updatedDocumentsList = updateDocumentsListRemoveStatus(
                updatedDocumentsList,
                newMasterDocumentDetails,
                oldReferenceActionItemId,
                event?.linkedAction?.actionType
            );
        }

        if (!isMasterDocUpdated && !!masterDocumentIdToRemove) {
            updatedDocumentsList = markDocumentToRemove(
                updatedDocumentsList,
                masterDocumentIdToRemove
            );
        }

        setDocumentDetailsList(() => updatedDocumentsList);
        return updatedDocumentsList;
    };

    const updateDocumentsListRemoveStatus = (
        documentsList: DocumentDetails[],
        newDocumentDetails: DocumentDetails,
        oldActionReferenceItemId?: string,
        actionType?: TimelineActionType
    ): DocumentDetails[] => {
        const updatedDocuments = documentsList
            .filter(
                (document) =>
                    !(
                        document.id === oldActionReferenceItemId &&
                        document.isUnregisteredFile
                    )
            )
            .map((document) =>
                document.id === oldActionReferenceItemId
                    ? {
                          ...document,
                          toRemove:
                              actionType !=
                              TimelineActionType.SellerEngagementDocument
                                  ? true
                                  : false,
                      }
                    : document
            );
        return updatedDocuments.concat(newDocumentDetails);
    };

    const markDocumentToRemove = (
        documentsList: DocumentDetails[],
        documentIdToRemove: string
    ): DocumentDetails[] => {
        const updatedDocuments = documentsList
            .filter(
                (document) =>
                    !(
                        document.id === documentIdToRemove &&
                        document.isUnregisteredFile
                    )
            )
            .map((document) =>
                document.id === documentIdToRemove
                    ? { ...document, toRemove: true }
                    : document
            );

        return updatedDocuments;
    };

    const handleProjectsChanges = (
        oldEv: TimelineEvent,
        action: TimelineAction,
        dataRequestProject: LinkedDataRequestProject
    ) => {
        let updatedProjects = [...dataRequestProjects];
        const isPrevDaqiAction =
            oldEv.linkedAction?.actionType ===
            TimelineActionType.SellerDataAndDocumentRequest;

        if (
            dataRequestProject?.id !== oldEv.linkedAction?.referenceItemId &&
            isPrevDaqiAction
        ) {
            updatedProjects = updatedProjects.filter(
                (p) => p.id !== oldEv.linkedAction?.referenceItemId
            );
        }

        if (
            action?.actionType ===
            TimelineActionType.SellerDataAndDocumentRequest
        ) {
            const existingProjectIndex = updatedProjects.findIndex(
                (p) => p.id === dataRequestProject?.id
            );
            if (existingProjectIndex > -1) {
                updatedProjects[existingProjectIndex] = dataRequestProject;
            } else {
                updatedProjects.push(dataRequestProject);
            }
        }
        if (
            action?.actionType !==
                TimelineActionType.SellerDataAndDocumentRequest &&
            isPrevDaqiAction
        ) {
            updatedProjects = updatedProjects.filter(
                (p) => p.id !== oldEv.linkedAction?.referenceItemId
            );
        }
        setDataRequestProjects(updatedProjects);
    };

    const updateTempo = () => {
        const updatedEvents = events.map((e) => {
            const newDuration =
                selectedTempo == TimelineTempo.SLOW
                    ? e.slowTempoDurationDays
                    : selectedTempo == TimelineTempo.MEDIUM
                    ? e.mediumTempoDurationDays
                    : e.fastTempoDurationDays;

            const newDependencies = e.dependencies.map((d) => {
                const newOffset =
                    selectedTempo == TimelineTempo.SLOW
                        ? d.slowTempoOffsetDays
                        : selectedTempo == TimelineTempo.MEDIUM
                        ? d.mediumTempoOffsetDays
                        : d.fastTempoOffsetDays;

                return { ...d, currentTempoOffsetDays: newOffset };
            });

            return {
                ...e,
                currentTempo: selectedTempo,
                currentTempoDurationDays: newDuration,
                dependencies: [...newDependencies],
            };
        });
        try {
            const sortedEvents = recalculateRows([...updatedEvents]);
            setEvents(sortedEvents);
            setCurrentTemplateTempo(selectedTempo);
        } catch (error) {
            createNotification((error as Error).message, 'error');
        }

        setTempoUpdateConfirmationModalVisible(false);
    };

    const renderLoader = () => {
        return (
            <div
                className="loader-container"
                style={{ width: '80vw', height: '40vh' }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Loading events...</h6>
                </div>
            </div>
        );
    };

    const renderDependencyRow = (
        dependency: TimelineEventDependencyFields,
        usedDependencies: TimelineEventDependencyFields[],
        index: number,
        count: number,
        remove: () => void,
        addNew: () => void
    ) => {
        const availableDependencies = events.filter((e) =>
            usedDependencies.every(
                (ud) =>
                    ud.dependencyEventId !== e.id ||
                    dependency.dependencyEventId === e.id
            )
        );

        return (
            <Row>
                <Col md={12}>
                    <Row>
                        <Col md={6}>
                            <label>Dependencies</label>
                            <div className="form-group">
                                <Input
                                    className="input-height"
                                    type="select"
                                    disabled={isArchived}
                                    onChange={(event) => {
                                        const selectedValue =
                                            event?.target.value;
                                        dependency.dependencyEventId =
                                            selectedValue;
                                    }}
                                    label="Dependencies"
                                    name={`dependencies.${index}.dependencyEventId`}
                                    placeholder="Select">
                                    {[
                                        {
                                            id: '',
                                            name: 'Select',
                                        },
                                        ...availableDependencies,
                                    ].map((item) => (
                                        <option
                                            value={item.id}
                                            key={'dependency' + item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </Input>
                            </div>
                        </Col>
                        <Col md={4}>
                            <Label>Offset (days)</Label>
                            <Field
                                className="input-height"
                                type="number"
                                min={0}
                                name={`dependencies.${index}.offsetDays`}
                                id="offset"
                                disabled={
                                    !dependency.dependencyEventId || isArchived
                                }
                                validate={(value) => {
                                    if (!dependency.dependencyEventId) {
                                        return undefined;
                                    }
                                    if (value < 0) {
                                        return 'The minimum value must be 0';
                                    }
                                    return undefined;
                                }}
                            />
                        </Col>
                        <Col md={2}>
                            <label className="invisible hide-on-small">
                                text
                            </label>
                            <div className="d-flex justify-content-around mt-md-2 change-on-small">
                                <FontAwesomeIcon
                                    className={`${
                                        isArchived ? '' : 'cursor-pointer'
                                    } ${
                                        availableDependencies.length > 0 &&
                                        count < MAX_DEPENDENCIES_AMOUNT &&
                                        count < availableDependencies.length
                                            ? ''
                                            : 'text-muted'
                                    }`}
                                    onClick={() => {
                                        if (
                                            !isArchived &&
                                            availableDependencies.length > 0 &&
                                            count < MAX_DEPENDENCIES_AMOUNT &&
                                            count < availableDependencies.length
                                        ) {
                                            addNew();
                                        }
                                    }}
                                    title={`${
                                        availableDependencies.length > 0 &&
                                        count < MAX_DEPENDENCIES_AMOUNT &&
                                        count < availableDependencies.length
                                            ? 'Add new dependency-offset pair'
                                            : `Maximum amount is ${
                                                  availableDependencies.length <
                                                  MAX_DEPENDENCIES_AMOUNT
                                                      ? availableDependencies.length <
                                                        2
                                                          ? 1
                                                          : availableDependencies.length
                                                      : MAX_DEPENDENCIES_AMOUNT
                                              }`
                                    }`}
                                    size="lg"
                                    icon={faPlus}
                                />
                                <FontAwesomeIcon
                                    className={`${
                                        isArchived ? '' : 'cursor-pointer'
                                    } ${
                                        count > 1
                                            ? 'delete-button'
                                            : 'text-muted'
                                    }`}
                                    onClick={() => {
                                        if (count > 1) remove();
                                    }}
                                    title={`${
                                        count > 1
                                            ? 'Remove dependency-offset pair'
                                            : 'At least one empty or filled pair must remain'
                                    }`}
                                    size="lg"
                                    icon={faTrashCan}
                                />
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    const getTimelinePropertiesRow = () => {
        return (
            <div>
                <Row className="">
                    <Col md={3}>
                        <label>Level of Service</label>
                        <div className="d-flex align-items-center justify-content-between">
                            {isTemplate ? (
                                <ReactstrapInput
                                    disabled={isArchived}
                                    className="input-height"
                                    type="select"
                                    onChange={(event) => {
                                        const selectedValue = parseInt(
                                            event?.target?.value
                                        );
                                        setLevelOfServiceToBeSet(selectedValue);
                                    }}
                                    label="Level of Service"
                                    id="level-of-service"
                                    placeholder="Select"
                                    value={currentLevelOfService}>
                                    {LevelsOfService.map((item) => (
                                        <option
                                            value={item.id}
                                            key={'levelOfService' + item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </ReactstrapInput>
                            ) : (
                                <>
                                    {
                                        LevelOfServiceOptions[
                                            currentLevelOfService
                                        ]
                                    }
                                </>
                            )}
                        </div>
                    </Col>
                    <Col md={3}>
                        <label>Tempo</label>
                        <div className="d-flex align-items-center justify-content-between">
                            <ReactstrapInput
                                disabled={isArchived}
                                className="input-height"
                                type="select"
                                onChange={(event) => {
                                    const selectedValue = parseInt(
                                        event?.target?.value
                                    );
                                    setSelectedTempo(selectedValue);
                                }}
                                label="Tempo"
                                id="tempo"
                                placeholder="Select"
                                value={selectedTempo}>
                                {[...TimelineTempos].map((item) => (
                                    <option
                                        value={item.id}
                                        key={'timelineTempo' + item.id}>
                                        {item.name}{' '}
                                        {currentTemplateTempo === item.id &&
                                            '(Current)'}
                                    </option>
                                ))}
                            </ReactstrapInput>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="label-placeholder"></div>
                        <div className="d-flex align-items-center justify-content-between">
                            <Button
                                className="btn btn-primary"
                                disabled={
                                    selectedTempo === currentTemplateTempo
                                }
                                onClick={() =>
                                    setTempoUpdateConfirmationModalVisible(true)
                                }>
                                Set as Current Tempo
                            </Button>
                        </div>
                    </Col>
                </Row>
                <hr />
            </div>
        );
    };

    const renderTimelineEditor = () => {
        return (
            <Fragment>
                <div
                    className={`cgm-admin-page timeline-editor ${
                        previewIsVisible ? 'timeline-editor-preview' : ''
                    }`}>
                    <div>
                        <div className="main-title-content">
                            <div className="title-breadcrumb">
                                <div className="page-title">
                                    <h3>Timeline Editor</h3>
                                </div>
                                <Breadcrumb>
                                    <BreadcrumbItem>
                                        <HomeButton
                                            onClick={() => {
                                                dispatch(
                                                    resetCurrentTimeline()
                                                );
                                            }}
                                        />
                                    </BreadcrumbItem>
                                    <BreadcrumbItem active>
                                        <span
                                            className="breadcrumb-link"
                                            onClick={() => {
                                                dispatch(
                                                    resetCurrentTimeline()
                                                );
                                                navigate(`/marketplace/admin`);
                                            }}>
                                            Marketplace Admin Home
                                        </span>
                                    </BreadcrumbItem>
                                    <BreadcrumbItem active>
                                        {editorType}
                                    </BreadcrumbItem>
                                </Breadcrumb>
                            </div>
                        </div>
                    </div>

                    <Card className="card-section main-card mb-3 gantt-chart-container">
                        <CardBody className="gantt-chart-container">
                            {previewIsVisible ? (
                                <>
                                    <div
                                        className="d-flex align-items-center justify-content-between"
                                        style={{
                                            margin: '0 20px',
                                        }}>
                                        <div>
                                            {isTemplate ? (
                                                <h5
                                                    style={{
                                                        textTransform:
                                                            'uppercase',
                                                    }}>
                                                    {timelineName}
                                                </h5>
                                            ) : (
                                                <div className="timeline-details">
                                                    <p className="practice-text">
                                                        <ShowFirstThenFullTextOnHoverLabel
                                                            items={
                                                                timelinePracticesName
                                                            }></ShowFirstThenFullTextOnHoverLabel>
                                                    </p>
                                                    <p className="template-text">
                                                        <span>
                                                            Timeline:{' '}
                                                            {timelineName},{' '}
                                                        </span>
                                                        <span>
                                                            Template:{' '}
                                                            {
                                                                timelineTemplateName
                                                            }
                                                        </span>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() =>
                                                setPreviewIsVisible(false)
                                            }>
                                            Back
                                        </button>
                                    </div>
                                    <MarketplaceTemplatePreviewComponent
                                        onEventClick={() => {}}
                                        events={events}
                                        displayCurrentDayIndicator={!isTemplate}
                                        startDate={timelineStartDate}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="timeline-details-row w-100 d-flex flex-column flex-lg-row justify-content-between align-items-center">
                                        <Col
                                            md={5}
                                            className="d-flex justify-content-between">
                                            {isTemplate ? (
                                                <EditableNameField
                                                    value={timelineName}
                                                    viewContent={
                                                        <div
                                                            className={`text-bold w-50 mb-2 mb-lg-0 d-flex align-items-center ${
                                                                !nameIsValid
                                                                    ? 'text-danger'
                                                                    : ''
                                                            }`}>
                                                            {timelineName}
                                                            {!nameIsValid && (
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faExclamationCircle
                                                                    }
                                                                    className="ml-2 text-danger"
                                                                    title="The name is not valid. Please choose another name."
                                                                />
                                                            )}
                                                        </div>
                                                    }
                                                    updateValue={(
                                                        newName: string
                                                    ) => doesNameExist(newName)}
                                                    defaultEditMode={
                                                        nameIsEditable
                                                    }
                                                    onEditModeChange={(
                                                        isEditing
                                                    ) =>
                                                        setNameIsEditable(
                                                            isEditing
                                                        )
                                                    }
                                                    nameIsValid={nameIsValid}
                                                />
                                            ) : (
                                                <div className="timeline-details">
                                                    <p className="practice-text">
                                                        <ShowFirstThenFullTextOnHoverLabel
                                                            items={
                                                                timelinePracticesName
                                                            }></ShowFirstThenFullTextOnHoverLabel>
                                                    </p>
                                                    <p className="template-text">
                                                        <span>
                                                            Template:{' '}
                                                            {
                                                                timelineTemplateName
                                                            }
                                                        </span>
                                                    </p>
                                                </div>
                                            )}
                                        </Col>

                                        <Col
                                            md={3}
                                            className="text-bold d-flex justify-content-center align-items-center">
                                            {'Projected process duration: '}
                                            {events[events.length - 1]
                                                ?.dayEnd || 0}
                                        </Col>

                                        <Col
                                            md={4}
                                            className="d-flex justify-content-end gap-2 w-lg-50 buttons-container">
                                            <button
                                                disabled={isLoading}
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setPreviewIsVisible(true);
                                                    setSelectedStage(0);
                                                }}>
                                                Preview
                                            </button>
                                            <button
                                                disabled={isLoading}
                                                className="btn btn-cancel ml-2"
                                                onClick={() => {
                                                    dispatch(
                                                        resetCurrentTimeline()
                                                    );
                                                    navigate({
                                                        pathname:
                                                            '/marketplace/admin',
                                                        search: `?${createSearchParams(
                                                            {
                                                                tab: Number(
                                                                    isTemplate
                                                                        ? AdminMarketplaceTab.TimelineTemplates
                                                                        : AdminMarketplaceTab.Engagements
                                                                ).toString(),
                                                            }
                                                        )}`,
                                                    });
                                                }}>
                                                Cancel
                                            </button>
                                            <ButtonLoader
                                                buttonText={'Save'}
                                                loaderButtonText={''}
                                                disabled={
                                                    !isSaveEnabled ||
                                                    isLoading ||
                                                    isArchived
                                                }
                                                isLoading={isLoading}
                                                onClick={handleSave}
                                                className={
                                                    'btn btn-primary ml-2'
                                                }
                                            />
                                        </Col>
                                    </div>

                                    <hr />

                                    {getTimelinePropertiesRow()}

                                    <div>
                                        <Form
                                            onSubmit={() => {}}
                                            initialValues={initialFormValues}
                                            validationSchema={validationSchema}>
                                            {({
                                                values,
                                                errors,
                                                touched,
                                                handleSubmit,
                                                handleChange,
                                                resetForm,
                                                isValid,
                                                dirty,
                                            }) => (
                                                <>
                                                    <Row>
                                                        <Col md={3}>
                                                            <label>
                                                                Event Name
                                                            </label>
                                                            <Field
                                                                className="input-height"
                                                                type="text"
                                                                name="eventName"
                                                                id="eventName"
                                                                placeholder="Event Name"
                                                                disabled={
                                                                    isArchived
                                                                }
                                                            />
                                                        </Col>

                                                        <Col md={3}>
                                                            <div className="d-flex justify-content-between ">
                                                                <div>
                                                                    <label>
                                                                        Stage
                                                                    </label>
                                                                </div>
                                                                <div className="d-flex mt-1 mr-1">
                                                                    <FontAwesomeIcon
                                                                        className="cursor-pointer"
                                                                        onClick={() =>
                                                                            setIsStageEditorModalVisible(
                                                                                true
                                                                            )
                                                                        }
                                                                        icon={
                                                                            faGear
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="form-group">
                                                                <Input
                                                                    disabled={
                                                                        stagesAreLoading ||
                                                                        isArchived
                                                                    }
                                                                    className="input-height"
                                                                    type="select"
                                                                    onChange={(
                                                                        event
                                                                    ) => {
                                                                        const selectedValue =
                                                                            parseInt(
                                                                                event
                                                                                    ?.target
                                                                                    ?.value
                                                                            );
                                                                        setSelectedStage(
                                                                            selectedValue
                                                                        );
                                                                    }}
                                                                    label="Stage"
                                                                    name="stage"
                                                                    id="stage"
                                                                    placeholder="Select">
                                                                    {[
                                                                        {
                                                                            id: '',
                                                                            name: 'Select',
                                                                        },
                                                                        ...stages,
                                                                    ].map(
                                                                        (
                                                                            item
                                                                        ) => (
                                                                            <option
                                                                                value={
                                                                                    item.id
                                                                                }
                                                                                key={
                                                                                    'practiceFocus' +
                                                                                    item.id
                                                                                }>
                                                                                {
                                                                                    item.name
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </Input>
                                                            </div>
                                                        </Col>

                                                        <Col md={3}>
                                                            <label>Party</label>
                                                            <div className="form-group">
                                                                <Input
                                                                    disabled={
                                                                        isArchived
                                                                    }
                                                                    className="input-height"
                                                                    type="select"
                                                                    label="Party"
                                                                    name="party"
                                                                    id="pary"
                                                                    placeholder="Seller">
                                                                    {[
                                                                        {
                                                                            id: '',
                                                                            name: 'Select',
                                                                        },
                                                                        ...TimelineParties.filter(
                                                                            (
                                                                                party
                                                                            ) =>
                                                                                party.id !==
                                                                                TimelineParty.Broker
                                                                        ),
                                                                    ].map(
                                                                        (
                                                                            item
                                                                        ) => (
                                                                            <option
                                                                                value={
                                                                                    item.id
                                                                                }
                                                                                key={
                                                                                    'practiceFocus' +
                                                                                    item.id
                                                                                }>
                                                                                {
                                                                                    item.name
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </Input>
                                                            </div>
                                                        </Col>
                                                        <Col md={3}>
                                                            <label>
                                                                Duration
                                                            </label>
                                                            <Field
                                                                className="input-height"
                                                                type="number"
                                                                name="duration"
                                                                id="duration"
                                                                placeholder="0"
                                                                disabled={
                                                                    isArchived
                                                                }
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md={6}>
                                                            <FieldArray name="dependencies">
                                                                {({
                                                                    remove,
                                                                    push,
                                                                }) =>
                                                                    values.dependencies.map(
                                                                        (
                                                                            dependency,
                                                                            index
                                                                        ) => {
                                                                            return renderDependencyRow(
                                                                                dependency,
                                                                                values.dependencies ??
                                                                                    [],
                                                                                index,
                                                                                values
                                                                                    .dependencies
                                                                                    .length,
                                                                                () =>
                                                                                    remove(
                                                                                        index
                                                                                    ),
                                                                                () =>
                                                                                    push(
                                                                                        {
                                                                                            dependencyEventId:
                                                                                                '',
                                                                                            offsetDays: 0,
                                                                                        }
                                                                                    )
                                                                            );
                                                                        }
                                                                    )
                                                                }
                                                            </FieldArray>
                                                        </Col>

                                                        <Col md={3} lg={4} />

                                                        <Col md={3} lg={2}>
                                                            <label className="invisible hide-on-small">
                                                                text
                                                            </label>
                                                            <div className="button-container">
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary input-height add-event-btn"
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        e.preventDefault();
                                                                        handleSubmit();
                                                                        if (
                                                                            isValid &&
                                                                            dirty
                                                                        ) {
                                                                            if (
                                                                                validateAndAddEvent(
                                                                                    values
                                                                                )
                                                                            )
                                                                                resetForm(
                                                                                    {
                                                                                        values: initialFormValues,
                                                                                    }
                                                                                );
                                                                        }
                                                                    }}
                                                                    disabled={
                                                                        !isValid ||
                                                                        !dirty ||
                                                                        isArchived
                                                                    }>
                                                                    Add Event
                                                                </button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                        </Form>
                                    </div>

                                    <hr />

                                    <div>
                                        {!initialDataLoading ? (
                                            <TimelineTable
                                                timelineId={Number(timelineId)}
                                                currentTempo={
                                                    currentTemplateTempo
                                                }
                                                rows={events}
                                                stages={stages}
                                                handleRemoveRow={
                                                    handleRemoveRow
                                                }
                                                handleUpdateRow={
                                                    handleUpdateRow
                                                }
                                                isLoading={stagesAreLoading}
                                                documentDetailsList={
                                                    documentDetailsList
                                                }
                                                dataRequestProjects={
                                                    dataRequestProjects
                                                }
                                                ndaTermSelectionState={{
                                                    id: ndaTermSelectionState?.id,
                                                    noNsTemplateId: Number(
                                                        ndaTermSelectionState?.noNsTemplateId ??
                                                            0
                                                    ),
                                                    dvmAndStaffNsTemplateId:
                                                        Number(
                                                            ndaTermSelectionState?.dvmAndStaffNsTemplateId ??
                                                                0
                                                        ),
                                                    dvmNsTemplateId: Number(
                                                        ndaTermSelectionState?.dvmNsTemplateId ??
                                                            0
                                                    ),
                                                    staffNsTemplateId: Number(
                                                        ndaTermSelectionState?.staffNsTemplateId ??
                                                            0
                                                    ),
                                                }}
                                                teaserData={teaserData}
                                                onTeaserSave={
                                                    saveTeaserToCurrentState
                                                }
                                                onUpdateConfirm={() =>
                                                    setisTeaserConfirmed(true)
                                                }
                                                isTeaserConfirmed={
                                                    isTeaserConfirmed
                                                }
                                                expressionOfInterestSettings={
                                                    expressionOfInterestSettings
                                                }
                                                isArchived={isArchived}
                                                isTemplate={isTemplate}
                                                documentsSections={
                                                    documentSectionList
                                                }
                                                setDocumentSectionList={
                                                    setDocumentSectionList
                                                }
                                                levelOfService={
                                                    currentLevelOfService
                                                }
                                            />
                                        ) : (
                                            renderLoader()
                                        )}
                                    </div>

                                    <div className="w-100 d-flex flex-column flex-md-row justify-content-between align-items-center mt-4">
                                        <div className="d-flex justify-content-end gap-2 w-100 w-md-auto buttons-container">
                                            <button
                                                disabled={isLoading}
                                                className="btn btn-cancel ml-2"
                                                onClick={() => {
                                                    dispatch(
                                                        resetCurrentTimeline()
                                                    );
                                                    navigate({
                                                        pathname:
                                                            '/marketplace/admin',
                                                        search: `?${createSearchParams(
                                                            {
                                                                tab: Number(
                                                                    isTemplate
                                                                        ? AdminMarketplaceTab.TimelineTemplates
                                                                        : AdminMarketplaceTab.Engagements
                                                                ).toString(),
                                                            }
                                                        )}`,
                                                    });
                                                }}>
                                                Cancel
                                            </button>
                                            <ButtonLoader
                                                buttonText={'Save'}
                                                loaderButtonText={''}
                                                disabled={
                                                    !isSaveEnabled || isLoading
                                                }
                                                isLoading={isSaving}
                                                onClick={handleSave}
                                                className={
                                                    'btn btn-primary ml-2'
                                                }
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>

                    <StageEditorModal
                        stagesUsedLocally={Array.from(
                            new Set([
                                ...events.map((event) => event.stage),
                                selectedStage,
                            ])
                        )}
                        isVisible={isStageEditorModalVisible}
                        onClose={() => setIsStageEditorModalVisible(false)}
                        setFlagToFetch={handleFlagState}
                    />
                    <div className="confirmation-modals">
                        {tempoUpdateConfirmationModalVisible && (
                            <ConfirmationModal
                                isVisible={tempoUpdateConfirmationModalVisible}
                                setIsVisible={
                                    setTempoUpdateConfirmationModalVisible
                                }
                                header={'Are you sure?'}
                                confirmationText={
                                    isTemplate
                                        ? 'This will update default tempo for the Template'
                                        : 'This will update the tempo for an Engagement without charging the Seller.'
                                }
                                onConfirm={updateTempo}
                                params={undefined}
                                nextButtonText="Confirm"
                                cancelButtonText="Cancel"></ConfirmationModal>
                        )}

                        {levelOfServiceToBeSet !== undefined && (
                            <ConfirmationModal
                                isVisible={levelOfServiceToBeSet !== undefined}
                                setIsVisible={() =>
                                    setLevelOfServiceToBeSet(undefined)
                                }
                                header={'Are you sure?'}
                                confirmationText="This will update Level of Service for the Template"
                                onConfirm={() => {
                                    setCurrentLevelOfService(
                                        levelOfServiceToBeSet
                                    );
                                    setLevelOfServiceToBeSet(undefined);
                                }}
                                params={undefined}
                                nextButtonText="Confirm"
                                cancelButtonText="Cancel"></ConfirmationModal>
                        )}
                    </div>
                </div>
            </Fragment>
        );
    };

    return (
        <Layout>
            {isAdmin ? (
                renderTimelineEditor()
            ) : (
                <h1>You don't have an access to see this page.</h1>
            )}
        </Layout>
    );
};

export default TimelineEditor;
